/*
 * What narrows and orders the list (`use-inbox-page.ts` holds the rest): the search, the filters,
 * the page, the order and the mode, and the saved view they came from. Opening a saved view loads
 * its query into these, so the field, the chips and the view menu say what the view is; leaving it
 * clears them. Whether the view on screen has changes is measured against what it loaded.
 */
import { useEffect, useState } from "react";
import { type MailChoice, mailHref, viewKey } from "../shell-nav.ts";
import {
  type ListFilters,
  type ListSort,
  useListMode,
  useListSort,
  useRememberPerson,
} from "./list-prefs.ts";
import { choiceQuery, NO_FILTERS, type SavedView, savedSort } from "./use-inbox.ts";
import { useSearch } from "./use-search.ts";
import { filtersOf, legacyTokens, viewQueryOf } from "./view-query.ts";

const anyFilter = (filters: ListFilters): boolean =>
  filters.status !== null ||
  filters.agent !== null ||
  filters.unread ||
  filters.days !== null ||
  filters.check ||
  filters.label !== null;

/** The filters as set, or the one empty value when none is on. */
function normalFilters(next: ListFilters): ListFilters {
  return anyFilter(next) ? next : NO_FILTERS;
}

/** A place that is itself a status: Meeting requests, Needs reply, Follow-up due. */
export const statusPlace = (choice: MailChoice): boolean =>
  choice.kind === "meeting" || choice.kind === "needs_reply" || choice.kind === "follow_up";

/**
 * The filters that apply to the list on screen. Needs reply is itself a status and shows no Status
 * filter; a label row is itself a label and shows no Label filter (LEA-485). One set elsewhere
 * neither narrows them nor counts as a filter there; it stays held, and applies again back in a list
 * that shows it.
 */
function shownFilters(choice: MailChoice, filters: ListFilters): ListFilters {
  if (statusPlace(choice)) return { ...filters, status: null, agent: null };
  if (choice.kind === "label") return { ...filters, label: null };
  return filters;
}

/**
 * Loads a saved view's query into the page when it is opened, once its list of views is known, and
 * clears what it loaded when the rep leaves it. True once the list may be asked for.
 */
function useViewLoading(
  choice: MailChoice,
  views: readonly SavedView[] | undefined,
  load: (view: SavedView | undefined) => void,
  leave: () => void,
): boolean {
  const [loaded, setLoaded] = useState<string | null>(null);
  const id = choice.kind === "view" ? choice.id : null;
  useEffect(() => {
    if (id === null) {
      if (loaded === null) return;
      setLoaded(null);
      return leave();
    }
    if (loaded === id || views === undefined) return;
    load(views.find((view) => view.id === id));
    setLoaded(id);
  });
  return id === null || loaded === id;
}

/** True when the view on screen differs from what it was when it had loaded and settled. */
function useViewChanged(viewId: string | null, settled: boolean, signature: string): boolean {
  const [baseline, setBaseline] = useState<{ id: string; signature: string } | null>(null);
  useEffect(() => {
    if (viewId !== null && settled && baseline?.id !== viewId)
      setBaseline({ id: viewId, signature });
  });
  return viewId !== null && baseline?.id === viewId && baseline.signature !== signature;
}

/*
 * A different list starts at its first page with nothing selected and nothing open: the address
 * no longer names the conversation that was, and a reload would not reopen it.
 */
export function useChoiceReset(choice: MailChoice | string, reset: () => void): void {
  const key = typeof choice === "string" ? choice : mailHref("", choice);
  const [shown, setShown] = useState(key);
  if (shown !== key) {
    setShown(key);
    reset();
  }
}

export type ListControls = ReturnType<typeof useListControls>;

export function useListControls(
  workspaceId: string,
  userId: string,
  choice: MailChoice,
  views: readonly SavedView[] | undefined,
) {
  const search = useSearch();
  const [filters, setFiltersState] = useState<ListFilters>(NO_FILTERS);
  const [offset, setOffset] = useState(0);
  const [mode, setMode] = useListMode(userId);
  useRememberPerson(userId);
  const saved = views ?? [];
  const [sort, chooseSort] = useListSort(
    userId,
    viewKey(workspaceId, choice),
    savedSort(choice, saved) ?? "newest",
  );
  const ready = useViewLoading(
    choice,
    views,
    (view) => {
      const query = view?.query ?? {};
      search.load(query.q ?? "", legacyTokens(query));
      setFiltersState(normalFilters(filtersOf(query)));
      setOffset(0);
    },
    () => {
      search.clear();
      setFiltersState(NO_FILTERS);
    },
  );
  // A new search starts at its first page, as a new filter does.
  useChoiceReset(search.q, () => setOffset(0));
  const base = choiceQuery(choice, saved);
  const shown = shownFilters(choice, filters);
  const viewQuery = viewQueryOf(base, search.q, shown, sort);
  const onView =
    choice.kind === "view" ? (saved.find((view) => view.id === choice.id) ?? null) : null;
  const changed = useViewChanged(
    onView?.id ?? null,
    ready && search.caughtUp,
    JSON.stringify(viewQuery),
  );
  return {
    search,
    q: search.q,
    /** As set, and kept across lists; the chips draw these. */
    filters,
    /** What narrows the list on screen (`shownFilters`); the query and a saved view take these. */
    shown,
    setFilters: (next: ListFilters) => {
      setFiltersState(normalFilters(next));
      setOffset(0);
    },
    filtered: search.q !== "" || anyFilter(shown),
    /** Operators or filters narrow the list on screen, so an empty one offers to take them off. */
    narrowed: search.tokens.length > 0 || anyFilter(shown),
    offset,
    setOffset,
    sort,
    setSort: (next: ListSort) => {
      chooseSort(next);
      setOffset(0);
    },
    mode,
    setMode,
    ready,
    viewQuery,
    /** The saved view on screen while it has changes, for "Save changes". */
    changedView: changed && onView ? { id: onView.id, name: onView.name } : null,
  };
}
